// Intent: agent-mode chat pane — the agent answers a client's message from the
// phone (stage B of agent mode). Server re-verifies on every call that this
// conversation belongs to one of the agent's clients (caretaker rule), so a
// stale/forged id degrades to the error card. 5 s focused polling, like the
// CRM pane; reading marks the conversation read (kills the CRM unread badge).

import { useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  ChatMessage,
  fetchAdminMessagesApi,
  fetchAgentMessagesApi,
  sendAdminReplyApi,
  sendAgentReplyApi,
} from '@/api/mobile-api';
import { ChatThread } from '@/components/chat-thread';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAuthStore } from '@/store/auth-store';
import { colors, spacing } from '@/theme/tokens';

const PANE_POLL_MS = 5000;

export default function AgentChatPaneScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const conversationId = Number(id);
  // Admin uses the all-chats endpoints (WWW conversations aren't in the
  // caretaker scope); the server re-verifies the role on every call.
  const isAdmin = useAuthStore((s) => s.isAdmin);

  const [title, setTitle] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [closed, setClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastIdRef = useRef(0);

  const mergeMessages = useCallback((incoming: ChatMessage[]) => {
    if (!incoming.length) return;
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      if (!fresh.length) return prev;
      const next = [...prev, ...fresh];
      lastIdRef.current = Math.max(lastIdRef.current, ...next.map((m) => m.id));
      return next;
    });
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (!Number.isFinite(conversationId)) {
        setError(t('agentMode.chat.loadError'));
        return undefined;
      }
      let cancelled = false;
      const load = async () => {
        const res = isAdmin
          ? await fetchAdminMessagesApi(conversationId, lastIdRef.current)
          : await fetchAgentMessagesApi(conversationId, lastIdRef.current);
        if (cancelled) return;
        if (res.ok && res.data) {
          mergeMessages(res.data.messages);
          setTitle(res.data.conversation.visitorName ?? `#${conversationId}`);
          setClosed(res.data.conversation.status === 'CLOSED');
          setError(null);
        } else if (!res.ok) {
          setError(res.message ?? t('agentMode.chat.loadError'));
        }
      };
      void load();
      const interval = setInterval(load, PANE_POLL_MS);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }, [conversationId, mergeMessages, t, isAdmin]),
  );

  const onSend = async (body: string) => {
    const res = isAdmin
      ? await sendAdminReplyApi(conversationId, body)
      : await sendAgentReplyApi(conversationId, body);
    if (res.ok && res.data) {
      mergeMessages([res.data]);
      setError(null);
    } else {
      setError(res.message ?? t('chat.sendError'));
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <View style={{ paddingHorizontal: spacing.screen, paddingTop: spacing.sm }}>
        <ScreenHeader title={title ?? t('agentMode.chat.paneTitle')} />
      </View>

      {error ? (
        <View style={{ paddingHorizontal: spacing.screen, paddingBottom: spacing.sm }}>
          <Card style={{ backgroundColor: colors.warningBg }}>
            <AppText variant="bodyLg" color={colors.warning}>
              {error}
            </AppText>
          </Card>
        </View>
      ) : null}

      <ChatThread messages={messages} me="AGENT" onSend={onSend} sendDisabled={closed} />
    </SafeAreaView>
  );
}
